import {
  deleteDoc,
  doc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type MessagePayload,
  type Messaging,
} from 'firebase/messaging';
import app, { db } from '@/lib/firebase';

const FCM_SW_PATH = '/firebase-messaging-sw.js';
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope';
const FCM_TOKEN_STORAGE_KEY = 'kulum:fcm:token';
const FCM_USER_STORAGE_KEY = 'kulum:fcm:user';

type PushChannelKey = 'messages' | 'profileRequests' | 'reviews';
type BadgeNavigator = Navigator & {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

export type FcmSyncStatus =
  | 'registered'
  | 'unregistered'
  | 'permission_default'
  | 'permission_denied'
  | 'unsupported'
  | 'missing_vapid_key'
  | 'error';

export interface SyncFcmOptions {
  userId: string;
  pushEnabled: boolean;
  allowPermissionPrompt?: boolean;
}

export interface ForegroundNotification {
  title: string;
  body?: string;
  openConversationId?: string;
}

export type NotificationPermissionState =
  | NotificationPermission
  | 'unsupported';

interface StoredToken {
  token: string;
  userId: string;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof navigator !== 'undefined';
}

function hasNotificationApi(): boolean {
  return isBrowser() && 'Notification' in window;
}

function hasServiceWorkerApi(): boolean {
  return isBrowser() && 'serviceWorker' in navigator;
}

let messagingSupportPromise: Promise<boolean> | null = null;

async function isMessagingAvailable(): Promise<boolean> {
  if (!hasNotificationApi() || !hasServiceWorkerApi()) {
    return false;
  }

  if (!messagingSupportPromise) {
    messagingSupportPromise = isSupported().catch(() => false);
  }

  return messagingSupportPromise;
}

function getVapidKey(): string {
  return import.meta.env.VITE_FIREBASE_VAPID_KEY?.trim() ?? '';
}

function getMessagingServiceWorkerUrl(): string {
  const params = new URLSearchParams({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  });

  return `${FCM_SW_PATH}?${params.toString()}`;
}

let fcmServiceWorkerRegistrationPromise: Promise<ServiceWorkerRegistration> | null = null;

async function getFcmServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  if (!hasServiceWorkerApi()) {
    throw new Error('Service Worker is not supported');
  }

  if (!fcmServiceWorkerRegistrationPromise) {
    fcmServiceWorkerRegistrationPromise = navigator.serviceWorker.register(
      getMessagingServiceWorkerUrl(),
      { scope: FCM_SW_SCOPE }
    );
  }

  return fcmServiceWorkerRegistrationPromise;
}

async function getMessagingInstance(): Promise<Messaging | null> {
  const available = await isMessagingAvailable();
  if (!available) {
    return null;
  }

  try {
    return getMessaging(app);
  } catch (error) {
    console.error('[FCM] Failed to create messaging instance:', error);
    return null;
  }
}

function getTokenDocumentId(token: string): string {
  return encodeURIComponent(token);
}

function readStoredToken(): StoredToken | null {
  if (!isBrowser()) {
    return null;
  }

  try {
    const token = window.localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
    const userId = window.localStorage.getItem(FCM_USER_STORAGE_KEY);
    if (!token || !userId) {
      return null;
    }

    return { token, userId };
  } catch (error) {
    console.warn('[FCM] Could not read stored token info:', error);
    return null;
  }
}

function writeStoredToken(value: StoredToken): void {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.setItem(FCM_TOKEN_STORAGE_KEY, value.token);
    window.localStorage.setItem(FCM_USER_STORAGE_KEY, value.userId);
  } catch (error) {
    console.warn('[FCM] Could not persist token info:', error);
  }
}

function clearStoredToken(): void {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(FCM_USER_STORAGE_KEY);
  } catch (error) {
    console.warn('[FCM] Could not clear token info:', error);
  }
}

async function saveTokenDocument(userId: string, token: string): Promise<void> {
  const tokenRef = doc(db, 'users', userId, 'fcmTokens', getTokenDocumentId(token));
  await setDoc(
    tokenRef,
    {
      token,
      device: 'web',
      userAgent: navigator.userAgent,
      createdAt: serverTimestamp(),
      lastUsedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

async function deleteTokenDocument(userId: string, token: string): Promise<void> {
  const tokenRef = doc(db, 'users', userId, 'fcmTokens', getTokenDocumentId(token));
  await deleteDoc(tokenRef);
}

async function clearStoredTokenByUserId(userId: string): Promise<void> {
  const storedToken = readStoredToken();
  if (!storedToken || storedToken.userId !== userId) {
    return;
  }

  try {
    await deleteTokenDocument(storedToken.userId, storedToken.token);
  } catch (error) {
    console.warn('[FCM] Could not delete token document:', error);
  } finally {
    clearStoredToken();
  }
}

export async function removeFcmRegistration(userId: string): Promise<void> {
  await clearStoredTokenByUserId(userId);

  const messaging = await getMessagingInstance();
  if (!messaging || !hasNotificationApi() || Notification.permission !== 'granted') {
    return;
  }

  try {
    await deleteToken(messaging);
  } catch (error) {
    console.warn('[FCM] Could not delete browser token:', error);
  }
}

export async function syncFcmRegistration(options: SyncFcmOptions): Promise<FcmSyncStatus> {
  const {
    userId,
    pushEnabled,
    allowPermissionPrompt = false,
  } = options;

  if (!userId) {
    return 'error';
  }

  if (!pushEnabled) {
    await removeFcmRegistration(userId);
    return 'unregistered';
  }

  const messaging = await getMessagingInstance();
  if (!messaging || !hasNotificationApi()) {
    return 'unsupported';
  }

  const vapidKey = getVapidKey();
  if (!vapidKey) {
    console.warn('[FCM] VITE_FIREBASE_VAPID_KEY is not configured.');
    return 'missing_vapid_key';
  }

  let permission = Notification.permission;
  if (permission === 'default' && allowPermissionPrompt) {
    permission = await Notification.requestPermission();
  }

  if (permission === 'denied') {
    await removeFcmRegistration(userId);
    return 'permission_denied';
  }

  if (permission !== 'granted') {
    return 'permission_default';
  }

  try {
    const registration = await getFcmServiceWorkerRegistration();
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });

    if (!token) {
      return 'error';
    }

    const storedToken = readStoredToken();
    if (storedToken && (storedToken.userId !== userId || storedToken.token !== token)) {
      await deleteTokenDocument(storedToken.userId, storedToken.token).catch((error) => {
        console.warn('[FCM] Could not clean previous token document:', error);
      });
    }

    await saveTokenDocument(userId, token);
    writeStoredToken({ userId, token });

    return 'registered';
  } catch (error) {
    console.error('[FCM] Failed to synchronize token:', error);
    return 'error';
  }
}

export async function subscribeToForegroundMessages(
  handler: (payload: MessagePayload) => void
): Promise<() => void> {
  const messaging = await getMessagingInstance();
  if (!messaging) {
    return () => undefined;
  }

  return onMessage(messaging, handler);
}

export function resolvePushEnabled(rawNotifications: unknown): boolean {
  if (typeof rawNotifications !== 'object' || rawNotifications === null) {
    return true;
  }

  const rawPush = (rawNotifications as { push?: unknown }).push;
  if (typeof rawPush !== 'object' || rawPush === null) {
    return true;
  }

  const push = rawPush as Partial<Record<PushChannelKey, unknown>>;
  const values = [push.messages, push.profileRequests, push.reviews];
  const hasBoolean = values.some((value) => typeof value === 'boolean');
  if (!hasBoolean) {
    return true;
  }

  return values.some((value) => value === true);
}

export function parseForegroundNotification(payload: MessagePayload): ForegroundNotification | null {
  const title = payload.notification?.title ?? payload.data?.title ?? 'KULUM';
  const body = payload.notification?.body ?? payload.data?.body;
  const openConversationId = payload.data?.openConversationId;

  if (!title && !body) {
    return null;
  }

  return {
    title,
    body,
    openConversationId: typeof openConversationId === 'string' ? openConversationId : undefined,
  };
}

export function getNotificationPermissionState(): NotificationPermissionState {
  if (!hasNotificationApi()) {
    return 'unsupported';
  }

  return Notification.permission;
}

export async function syncAppBadgeCount(count: number): Promise<void> {
  if (!isBrowser()) {
    return;
  }

  const nav = navigator as BadgeNavigator;
  if (typeof nav.setAppBadge !== 'function') {
    return;
  }

  try {
    if (count > 0) {
      await nav.setAppBadge(count);
      return;
    }

    if (typeof nav.clearAppBadge === 'function') {
      await nav.clearAppBadge();
      return;
    }

    await nav.setAppBadge(0);
  } catch (error) {
    console.warn('[FCM] Failed to synchronize app badge count:', error);
  }
}
